/**
 * Admin Usage Overview Page
 *
 * Shows system-wide usage analytics for administrators.
 * Route: /admin/usage
 */

import {
  useGetAdminUsageOverviewQuery,
  getActionLabel,
  type UsageAction,
} from '../../store/api/usageApi';

export function AdminUsageOverviewPage() {
  const { data, isLoading, isError, refetch } = useGetAdminUsageOverviewQuery();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="mt-1 text-gray-500">
            System-wide usage overview and credit consumption
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn-secondary text-sm"
        >
          Refresh
        </button>
      </div>

      {/* In-memory warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-medium">In-Memory Analytics</p>
            <p className="mt-1">
              {data?.meta.note || 'Data is stored in-memory and resets on server restart.'}
              {data?.meta.logCount !== undefined && (
                <span className="ml-2 text-amber-600">
                  ({data.meta.logCount.toLocaleString()} records)
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800">Failed to load usage analytics. Please try again.</p>
          <button onClick={() => refetch()} className="mt-3 btn-primary text-sm">
            Retry
          </button>
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Total Credits Consumed"
              value={data.totalCreditsConsumed}
              icon={<CreditIcon />}
              color="blue"
            />
            <StatCard
              title="Active Users"
              value={data.topUsersByCredits.length}
              icon={<UsersIcon />}
              color="green"
            />
            <StatCard
              title="Action Types"
              value={Object.keys(data.totalsByAction).length}
              icon={<ActionIcon />}
              color="purple"
            />
          </div>

          {/* Usage by Action Type */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Usage by Action Type</h2>
            </div>
            <div className="card-body">
              {Object.keys(data.totalsByAction).length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(data.totalsByAction)
                    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                    .map(([action, count]) => (
                      <div
                        key={action}
                        className="p-4 bg-gray-50 rounded-lg text-center"
                      >
                        <p className="text-2xl font-bold text-gray-900">{count}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {getActionLabel(action as UsageAction)}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  No usage recorded yet.
                </p>
              )}
            </div>
          </div>

          {/* Daily Usage Chart Placeholder / Table */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Daily Usage (Last 30 Days)</h2>
            </div>
            <div className="card-body">
              {data.daily.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Total Credits
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Actions Breakdown
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.daily.map((day) => (
                        <tr key={day.date} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                            {formatDate(day.date)}
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {day.totalCredits}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(day.totalByAction).map(([action, count]) => (
                                <span
                                  key={action}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                                >
                                  {getActionLabel(action as UsageAction)}: {count}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No daily usage data available yet.
                </p>
              )}
            </div>
          </div>

          {/* Top Users by Credits */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Top Users by Credits Consumed</h2>
            </div>
            <div className="card-body">
              {data.topUsersByCredits.length > 0 ? (
                <div className="space-y-3">
                  {data.topUsersByCredits.map((user, index) => (
                    <div
                      key={user.userId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold ${
                            index === 0
                              ? 'bg-yellow-100 text-yellow-700'
                              : index === 1
                                ? 'bg-gray-200 text-gray-700'
                                : index === 2
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-700 font-mono">
                          {user.userId.slice(0, 8)}...
                        </span>
                      </div>
                      <span className="font-medium text-gray-900">
                        {user.totalCredits} credits
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  No user usage data available yet.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Helper components

function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons

function CreditIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
      />
    </svg>
  );
}

function ActionIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

// Date formatting helper

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
